import { useEffect, useState } from 'react'
import type { FormInstance } from 'antd'
import {
  getEdsByUserLogged,
  GetEmpresasByOrganizacion,
  GetEmpresasByRegionales,
  getRegionalesByNegocio,
} from '../services/ConfiguracionRecursos.service'
import { FIELDS_NAMES_RECURSO } from '../interfaces/FieldNamesRecurso'

export interface RegionalOption {
  label: string
  value: number
}

export interface OrganizacionOption {
  label: string
  value: number
}

export interface EmpresaOption {
  label: string
  value: number
  id_regional_negocio?: number
}

interface UseUbicacionEstacionesParams {
  form: FormInstance
  /** El modal está abierto: se cargan los catálogos. */
  activo: boolean
  /** Solo en creación se ofrecen estaciones para elegir. */
  modoCreacion: boolean
  negocio: any
  empresasHijos: any
  /** Organizaciones del usuario (useGenericInfo.organizationByUser). */
  organizacionesOptions: OrganizacionOption[]
}

/**
 * Maneja la cascada Organización -> Regional -> Estación (empresa) del paso 1
 * del wizard de recursos.
 *
 * Organización y regionales son opcionales y solo sirven para acotar y
 * autoseleccionar estaciones:
 *  - Con organización y sin regionales: se listan y autoseleccionan todas las
 *    estaciones de esa organización.
 *  - Con regionales (con o sin organización): se listan y autoseleccionan todas
 *    las estaciones de esas regionales. Si no se eligió organización se usan
 *    todas las del usuario.
 *  - Sin organización ni regionales: se ofrecen las estaciones del usuario sin
 *    autoseleccionar nada, para que elija manualmente.
 *
 * El recurso se guarda por estación, no por pantalla: se envía el listado de
 * empresas y el backend resuelve la asignación.
 *
 * Organización/Regional/Estación viven como campos del `form` compartido, porque
 * `SelectPrefixForm` se auto-vincula por `name` a ese form.
 */
const useUbicacionEstaciones = ({
  form,
  activo,
  modoCreacion,
  negocio,
  empresasHijos,
  organizacionesOptions,
}: UseUbicacionEstacionesParams) => {
  const [regionalesOptions, setRegionalesOptions] = useState<RegionalOption[]>([])
  const [loadingRegionales, setLoadingRegionales] = useState(false)

  const [empresasOptions, setEmpresasOptions] = useState<EmpresaOption[]>([])
  const [loadingEmpresas, setLoadingEmpresas] = useState(false)

  // Se avisa al usuario cuando la selección de estaciones fue automática.
  const [autoseleccionadas, setAutoseleccionadas] = useState(false)

  const cargarRegionales = () => {
    setLoadingRegionales(true)
    getRegionalesByNegocio(negocio)
      .then((data: any) => {
        const regionales: RegionalOption[] = (data ?? []).map((d: any) => ({
          value: d.id_regional,
          label: d.nombre_regional,
        }))
        setRegionalesOptions(regionales)
      })
      .catch(() => setRegionalesOptions([]))
      .finally(() => setLoadingRegionales(false))
  }

  const aplicarEmpresas = (empresas: EmpresaOption[], autoseleccionar: boolean) => {
    setEmpresasOptions(empresas)
    setAutoseleccionadas(autoseleccionar && empresas.length > 0)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.EMPRESAS]: autoseleccionar ? empresas.map((empresa) => empresa.value) : [],
    })
  }

  /** Estaciones del usuario logueado: la lista base cuando no se filtra nada. */
  const cargarEmpresasBase = async () => {
    setLoadingEmpresas(true)
    try {
      const data: EmpresaOption[] = await getEdsByUserLogged(empresasHijos)
      aplicarEmpresas(data ?? [], false)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const cargarEmpresasPorRegionales = async (regionales: number[], organizacionId?: number | null) => {
    // Sin organización elegida se consultan todas las del usuario: las
    // estaciones de la regional se autoseleccionan igual.
    const organizaciones = organizacionId
      ? [organizacionId]
      : organizacionesOptions.map((organizacion) => organizacion.value)

    setLoadingEmpresas(true)
    try {
      const response = await GetEmpresasByRegionales(regionales, organizaciones)
      const empresas: EmpresaOption[] = (response.data?.body ?? []).map((e: any) => ({
        label: e.alias ?? e.razon_social,
        value: e.id,
        id_regional_negocio: e.id_regional_negocio,
      }))
      aplicarEmpresas(empresas, true)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  const cargarEmpresasPorOrganizacion = async (organizacionId: number) => {
    setLoadingEmpresas(true)
    try {
      const response = await GetEmpresasByOrganizacion(organizacionId)
      // La respuesta llega como array directo de { id, nit, razon_social }.
      const data = Array.isArray(response.data) ? response.data : (response.data?.data ?? response.data?.body ?? [])
      const empresas: EmpresaOption[] = (data ?? []).map((e: any) => ({
        label: e.razon_social ?? e.alias ?? '',
        value: Number(e.id),
      }))
      aplicarEmpresas(empresas, true)
    } catch (error) {
      aplicarEmpresas([], false)
    } finally {
      setLoadingEmpresas(false)
    }
  }

  /** Resuelve qué estaciones ofrecer según la combinación organización/regionales. */
  const recargarEmpresas = (organizacionId: number | null, regionales: number[]) => {
    if (regionales.length > 0) return cargarEmpresasPorRegionales(regionales, organizacionId)
    if (organizacionId) return cargarEmpresasPorOrganizacion(organizacionId)
    return cargarEmpresasBase()
  }

  // Al abrir el modal se cargan las regionales en cualquier modo (en editar/ver
  // sirven para mostrar la regional de la estación con su nombre); las
  // estaciones para elegir solo aplican en creación.
  useEffect(() => {
    if (!activo) return
    cargarRegionales()
    if (modoCreacion) cargarEmpresasBase()
  }, [activo, modoCreacion])

  const reset = () => {
    setRegionalesOptions([])
    setEmpresasOptions([])
    setAutoseleccionadas(false)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.ORGANIZACION]: null,
      [FIELDS_NAMES_RECURSO.REGIONALES]: [],
      [FIELDS_NAMES_RECURSO.EMPRESAS]: [],
    })
  }

  /**
   * editar/ver: la ubicación no se elige, viene fija del recurso. Se registra la
   * estación como única opción para que el select la muestre bloqueada con su
   * nombre, y la regional solo si el detalle la trae.
   */
  const fijarUbicacion = (estacion: EmpresaOption, regionalId?: number | null) => {
    setEmpresasOptions([estacion])
    setAutoseleccionadas(false)
    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.EMPRESAS]: [estacion.value],
      [FIELDS_NAMES_RECURSO.REGIONALES]: regionalId ? [regionalId] : [],
    })
  }

  const onChangeOrganizacion = (organizacionId: number | null) => {
    // Al cambiar de organización las regionales elegidas dejan de aplicar.
    form.setFieldsValue({ [FIELDS_NAMES_RECURSO.REGIONALES]: [] })
    recargarEmpresas(organizacionId ?? null, [])
  }

  const onChangeRegionales = (regionales: number[]) => {
    const organizacionId: number | null = form.getFieldValue(FIELDS_NAMES_RECURSO.ORGANIZACION) ?? null
    recargarEmpresas(organizacionId, regionales ?? [])
  }

  return {
    regionalesOptions,
    loadingRegionales,
    empresasOptions,
    loadingEmpresas,
    autoseleccionadas,
    onChangeOrganizacion,
    onChangeRegionales,
    fijarUbicacion,
    reset,
  }
}

export default useUbicacionEstaciones
